import { z } from 'zod';
import { AuditedFields, CursorPageQuery, IsoDate, IsoDateTime, Uuid, Version } from './common.js';
import { CustomerSummary } from './customers.js';
import { PricingUnit } from './resources.js';
import { DiscountType, ReservationLineKind } from './reservations.js';
/** Cycle de vie proforma (DOMAIN_ARCHITECTURE §6). */
export const ProformaStatus = z.enum(['DRAFT', 'ISSUED', 'ACCEPTED', 'EXPIRED', 'CANCELLED']);
/** Cycle de vie facture : ISSUED est immuable (R-003) ; l'annulation passe par un avoir. */
export const InvoiceStatus = z.enum(['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'CANCELLED']);
export const CreditNoteStatus = z.enum(['DRAFT', 'ISSUED']);
export const BillingLine = z.object({
    id: Uuid,
    position: z.number().int(),
    kind: ReservationLineKind,
    label: z.string(),
    description: z.string().nullable(),
    quantity: z.number().positive(),
    unit: PricingUnit.nullable(),
    unitPrice: z.number(),
    amount: z.number(),
    taxRuleCodes: z.array(z.string()),
});
export const BillingLineInput = z.object({
    kind: ReservationLineKind.default('EXTRA'),
    label: z.string().min(1).max(160),
    description: z.string().max(500).nullable().optional(),
    quantity: z.number().positive().default(1),
    unit: PricingUnit.nullable().optional(),
    unitPrice: z.number(),
});
export const TaxDetail = z.object({
    code: z.string(),
    label: z.string(),
    kind: z.enum(['TAX', 'WITHHOLDING']),
    rate: z.number(),
    base: z.number(),
    amount: z.number(),
});
/** Totaux communs (Sous-total − Remise + Taxes − Retenue = Net, ADR-009). */
export const BillingTotals = z.object({
    currencyCode: z.string().length(3),
    subtotal: z.number(),
    discountType: DiscountType,
    discountValue: z.number(),
    discountTotal: z.number(),
    taxTotal: z.number(),
    withholdingApplied: z.boolean(),
    withholdingRate: z.number().nullable(),
    withholdingTotal: z.number(),
    netTotal: z.number(),
    paidTotal: z.number(),
    balance: z.number(),
    /** « Arrêté la présente facture à la somme de … » */
    amountInWords: z.string(),
});
/** Snapshot de l'émetteur figé dans le document (raison sociale, RCCM, IFU, régime, banque…). */
export const IssuerSnapshot = z.object({
    legalName: z.string(),
    tradeName: z.string().nullable(),
    address: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    rccm: z.string().nullable(),
    ifu: z.string().nullable(),
    taxRegime: z.string().nullable(),
    taxDivision: z.string().nullable(),
    bankName: z.string().nullable(),
    bankAccount: z.string().nullable(),
    signatory: z.string().nullable(),
    logoUrl: z.string().nullable(),
    /** Ligne d'activité (T-H25) ; absente des snapshots figés avant son introduction. */
    activity: z.string().nullable().optional(),
});
const BillingDocumentBase = z.object({
    id: Uuid,
    number: z.string().nullable(),
    reservationId: Uuid.nullable(),
    reservationReference: z.string().nullable(),
    propertyId: Uuid,
    billingParty: CustomerSummary,
    billingPartyDetails: z.object({
        address: z.string().nullable(),
        ifu: z.string().nullable(),
        rccm: z.string().nullable(),
        taxRegime: z.string().nullable(),
        taxDivision: z.string().nullable(),
    }),
    guest: CustomerSummary.nullable(),
    issueDate: IsoDate.nullable(),
    placeOfIssue: z.string().nullable(),
    stayFrom: IsoDateTime.nullable(),
    stayTo: IsoDateTime.nullable(),
    subject: z.string().nullable(),
    lines: z.array(BillingLine),
    totals: BillingTotals,
    taxDetails: z.array(TaxDetail),
    notes: z.string().nullable(),
    pdfDocumentId: Uuid.nullable(),
    version: Version,
});
export const Proforma = BillingDocumentBase.extend({
    status: ProformaStatus,
    validUntil: IsoDate.nullable(),
    termsText: z.string().nullable(),
    acceptedAt: IsoDateTime.nullable(),
    convertedInvoiceId: Uuid.nullable(),
    cancellationReason: z.string().nullable(),
}).merge(AuditedFields);
export const ProformaListItem = Proforma.pick({
    id: true,
    number: true,
    status: true,
    billingParty: true,
    reservationReference: true,
    issueDate: true,
    validUntil: true,
    totals: true,
    convertedInvoiceId: true,
    createdAt: true,
});
export const Invoice = BillingDocumentBase.extend({
    status: InvoiceStatus,
    dueDate: IsoDate.nullable(),
    paymentTermsText: z.string().nullable(),
    issuer: IssuerSnapshot.nullable(),
    issuedAt: IsoDateTime.nullable(),
    issuedByName: z.string().nullable(),
    proformaId: Uuid.nullable(),
    proformaNumber: z.string().nullable(),
    /** Dérogation R-001 (D-017) : tracée sur la facture, jamais imprimée sur le PDF client. */
    complianceOverridden: z.boolean(),
    complianceOverrideReason: z.string().nullable(),
    complianceOverrideByName: z.string().nullable(),
    pdfSha256: z.string().nullable(),
    creditNotes: z.array(z.object({
        id: Uuid,
        number: z.string().nullable(),
        status: CreditNoteStatus,
        netTotal: z.number(),
        issueDate: IsoDate.nullable(),
    })),
    cancelledAt: IsoDateTime.nullable(),
}).merge(AuditedFields);
export const InvoiceListItem = Invoice.pick({
    id: true,
    number: true,
    status: true,
    billingParty: true,
    guest: true,
    reservationReference: true,
    issueDate: true,
    dueDate: true,
    totals: true,
    complianceOverridden: true,
    createdAt: true,
});
export const CreditNote = z
    .object({
    id: Uuid,
    number: z.string().nullable(),
    status: CreditNoteStatus,
    invoiceId: Uuid,
    invoiceNumber: z.string().nullable(),
    billingParty: CustomerSummary,
    reason: z.string(),
    issueDate: IsoDate.nullable(),
    lines: z.array(BillingLine),
    totals: BillingTotals.omit({ paidTotal: true, balance: true }),
    taxDetails: z.array(TaxDetail),
    isFull: z.boolean(),
    pdfDocumentId: Uuid.nullable(),
    issuedByName: z.string().nullable(),
    version: Version,
})
    .merge(AuditedFields);
/** Création d'un brouillon : depuis une réservation (lignes pré-remplies) ou libre. */
const DocumentDraftInput = z.object({
    reservationId: Uuid.nullable().optional(),
    billingPartyId: Uuid.optional(),
    guestId: Uuid.nullable().optional(),
    propertyId: Uuid.optional(),
    subject: z.string().max(200).nullable().optional(),
    stayFrom: IsoDateTime.nullable().optional(),
    stayTo: IsoDateTime.nullable().optional(),
    /** Si absent avec `reservationId` : lignes générées depuis la réservation. */
    lines: z.array(BillingLineInput).optional(),
    discountType: DiscountType.optional(),
    discountValue: z.number().nonnegative().optional(),
    applyWithholding: z.boolean().optional(),
    notes: z.string().max(2000).nullable().optional(),
});
export const CreateProformaRequest = DocumentDraftInput.extend({
    validUntil: IsoDate.nullable().optional(),
    termsText: z.string().max(2000).nullable().optional(),
});
export const UpdateProformaRequest = CreateProformaRequest.omit({
    reservationId: true,
    propertyId: true,
})
    .partial()
    .extend({ version: Version });
export const CreateInvoiceRequest = DocumentDraftInput.extend({
    proformaId: Uuid.nullable().optional(),
    dueDate: IsoDate.nullable().optional(),
    paymentTermsText: z.string().max(2000).nullable().optional(),
});
export const UpdateInvoiceRequest = CreateInvoiceRequest.omit({
    reservationId: true,
    propertyId: true,
    proformaId: true,
})
    .partial()
    .extend({ version: Version });
export const IssueProformaRequest = z.object({ version: Version });
export const AcceptProformaRequest = z.object({
    version: Version,
    acceptedAt: IsoDateTime.optional(),
});
export const CancelProformaRequest = z.object({
    version: Version,
    reason: z.string().min(3).max(500),
});
/** Conversion proforma → brouillon de facture (lien conservé). */
export const ConvertProformaRequest = z.object({
    version: Version,
    dueDate: IsoDate.nullable().optional(),
});
/** Émission d'une facture définitive : R-001, numérotation FAC, snapshot, PDF. */
export const IssueInvoiceRequest = z.object({
    version: Version,
    /** Dérogation R-001 (permission `invoice.issue.override_compliance`, Owner/Manager, motif ≥ 20 caractères — D-017/D-026). */
    complianceOverride: z
        .object({
        reason: z.string().min(20).max(1000),
        documentsExpectedBy: IsoDate.nullable().optional(),
    })
        .optional(),
});
export const CreateCreditNoteRequest = z.object({
    invoiceId: Uuid,
    reason: z.string().min(5).max(500),
    /** Avoir total (annule la facture) ou partiel avec lignes. */
    full: z.boolean().default(false),
    lines: z.array(BillingLineInput).optional(),
});
export const IssueCreditNoteRequest = z.object({ version: Version });
export const ListProformasQuery = CursorPageQuery.extend({
    status: ProformaStatus.optional(),
    q: z.string().max(100).optional(),
    billingPartyId: Uuid.optional(),
    reservationId: Uuid.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
export const ListInvoicesQuery = CursorPageQuery.extend({
    status: z.string().optional(),
    q: z.string().max(100).optional(),
    billingPartyId: Uuid.optional(),
    reservationId: Uuid.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    balanceDue: z.coerce.boolean().optional(),
    overdue: z.coerce.boolean().optional(),
});
export const ListCreditNotesQuery = CursorPageQuery.extend({
    invoiceId: Uuid.optional(),
    status: CreditNoteStatus.optional(),
});
/** Paramètres de facturation du tenant (textes légaux, échéances, signataire). */
export const BillingSettings = z.object({
    proformaValidityDays: z.number().int().min(1).max(365),
    invoiceDueDays: z.number().int().min(0).max(365),
    placeOfIssue: z.string().max(80),
    signatoryName: z.string().max(120),
    proformaTermsText: z.string().max(2000),
    invoicePaymentTermsText: z.string().max(2000),
    paymentMethodsText: z.string().max(2000),
    footerNote: z.string().max(500).nullable(),
    numbering: z.object({
        proforma: z.string(),
        invoice: z.string(),
        creditNote: z.string(),
        /** Référence des commandes POS (`CMD-{YYYY}-{NNNNNN}`), H2. */
        order: z.string(),
        /** Bons d'achat de stock (`PUR-{YYYY}-{NNNNNN}`), H5. */
        purchase: z.string(),
        /** Inventaires physiques (`CNT-{YYYY}-{NNNNNN}`), H5. */
        count: z.string(),
        /** Factures d'abonnement émises par la plateforme (`ABO/{YYYY}/{NNNNN}`), H8 — tenant `platform` seulement. */
        subscriptionInvoice: z.string(),
        resetYearly: z.boolean(),
    }),
    version: Version,
});
/**
 * Format d'un numéro de document (ADR-008) : majuscules, chiffres, `/ - _ .`,
 * `{YYYY}` facultatif et exactement un bloc `{NNN…}` (3 à 8 chiffres).
 * Ex. `FAC/{YYYY}/{NNNNN}`, `INV-{YYYY}-{NNNNNN}`.
 */
export const DocumentNumberFormat = z
    .string()
    .min(5)
    .max(40)
    .regex(/^[A-Z0-9][A-Z0-9/_.-]*(?:\{YYYY\}[A-Z0-9/_.-]*)?\{N{3,8}\}[A-Z0-9/_.-]*$/, {
    message: 'Format attendu : PREFIXE/{YYYY}/{NNNNN}',
})
    .refine((value) => (value.match(/\{N+\}/g) ?? []).length === 1, {
    message: 'Un seul bloc {NNNNN}',
});
export const UpdateBillingSettingsRequest = BillingSettings.omit({ numbering: true })
    .partial()
    .extend({
    version: Version,
    /** Formats modifiables ; un nouveau format s'applique aux séquences créées ensuite (exercice suivant). */
    numbering: z
        .object({
        proforma: DocumentNumberFormat.optional(),
        invoice: DocumentNumberFormat.optional(),
        creditNote: DocumentNumberFormat.optional(),
        order: DocumentNumberFormat.optional(),
        purchase: DocumentNumberFormat.optional(),
        count: DocumentNumberFormat.optional(),
        subscriptionInvoice: DocumentNumberFormat.optional(),
    })
        .optional(),
});
/** Synthèse financière d'une réservation (onglet Client & facturation). */
export const ReservationBillingSummary = z.object({
    reservationId: Uuid,
    proformas: z.array(ProformaListItem),
    invoices: z.array(InvoiceListItem),
    depositsTotal: z.number(),
    invoicedTotal: z.number(),
    paidTotal: z.number(),
    balance: z.number(),
    canIssueInvoice: z.boolean(),
    invoiceBlockedReason: z.enum(['COMPLIANCE_INCOMPLETE', 'ALREADY_INVOICED', 'STATUS']).nullable(),
    missingDocuments: z.array(z.string()),
});
//# sourceMappingURL=billing.js.map